import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Truck, Loader2, LogOut, Clock, MapPin, Star, Gift, RefreshCw, Save, History as HistoryIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/dashboard");
      setData(data);
      setProfile(data.profile);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your account");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const renew = async (id) => {
    setRenewing(id);
    try {
      const { data: res } = await api.post(`/driver/sessions/${id}/renew`);
      toast.success(`Renewed — charged $${res.charged.toFixed(2)} (simulated)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Renew failed");
    } finally {
      setRenewing(null);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/driver/profile", {
        name: profile.name, email: profile.email, plate: profile.plate,
        companyName: profile.companyName, truckType: profile.truckType, trailerType: profile.trailerType,
        mcNumber: profile.mcNumber, dotNumber: profile.dotNumber, licNumber: profile.licNumber,
      });
      toast.success("Profile saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const loyalty = data?.loyalty;

  return (
    <div data-testid="driver-portal" className="min-h-screen bg-slate-50 pb-16">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#A0148C]">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-sm font-black uppercase text-slate-900">{data?.businessName || "Discount Truck Parking"}</p>
              <p className="text-xs font-bold tracking-widest text-[#A0148C]">DRIVER PORTAL</p>
            </div>
          </div>
          <Button data-testid="driver-logout" variant="ghost" onClick={handleLogout} className="gap-2 text-slate-600">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
            Hi {profile?.name?.split(" ")[0] || "Driver"} 👋
          </h1>
          <p className="text-slate-500">Manage your parking, renewals, and rewards.</p>
        </div>

        {/* Loyalty */}
        {loyalty && (
          <Card data-testid="driver-loyalty" className="border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#A0148C]/10">
                  <Star className="h-6 w-6 text-[#A0148C]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Rewards — {loyalty.tier} tier</p>
                  <p className="font-heading text-2xl font-black text-slate-900">{loyalty.pointsBalance ?? 0} pts</p>
                </div>
              </div>
              {(loyalty.availableFreeNights || 0) > 0 && (
                <div className="flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 font-bold text-emerald-700">
                  <Gift className="h-5 w-5" /> {loyalty.availableFreeNights} free night{loyalty.availableFreeNights > 1 ? "s" : ""} available
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Active stays */}
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <Clock className="h-5 w-5 text-[#A0148C]" /> Current Parking
          </h2>
          {data?.activeSessions?.length ? (
            <div className="space-y-4">
              {data.activeSessions.map((s) => (
                <Card key={s.id} data-testid={`driver-active-${s.id}`} className="border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
                        <MapPin className="h-4 w-4 text-[#A0148C]" /> {s.lotName} · Spot #{s.spotNumber}
                      </p>
                      <p className="text-sm text-slate-500">{s.lotAddress}</p>
                      {s.ticketNumber && (
                        <p data-testid={`driver-ticket-${s.id}`} className="mt-1 font-data text-sm font-bold text-[#A0148C]">
                          Ticket #{s.ticketNumber}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-slate-600">
                        Expires {new Date(s.expiresAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                      {s.overstayed && <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">OVERSTAY</span>}
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${s.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {s.status === "Active" ? "Active" : "Overstay"}
                    </span>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 font-data text-sm">
                    <Row label="Base (1 day)" value={`$${s.renewPreview.base.toFixed(2)}`} />
                    <Row label={`Sales tax (${s.renewPreview.taxPercent ?? 0}%)`} value={`$${s.renewPreview.tax.toFixed(2)}`} />
                    <div className="mt-2 border-t border-slate-200 pt-2">
                      <Row label="Renew total" value={`$${s.renewPreview.total.toFixed(2)}`} bold />
                    </div>
                  </div>

                  <Button
                    data-testid={`driver-renew-${s.id}`}
                    onClick={() => renew(s.id)}
                    disabled={renewing === s.id}
                    className="mt-4 h-12 w-full gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]"
                  >
                    {renewing === s.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
                    Renew &amp; Pay (add 24h)
                  </Button>
                  <p className="mt-2 text-center text-xs text-slate-400">Payments are simulated in demo mode.</p>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-slate-200 bg-white p-6 text-center text-slate-500">
              No active parking right now. See you at the yard!
            </Card>
          )}
        </section>

        {/* Profile */}
        <section>
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">My Details</h2>
          <Card className="border-slate-200 bg-white p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <PField label="Name" testid="driver-profile-name" value={profile?.name} onChange={(v) => setP("name", v)} />
              <PField label="Email" testid="driver-profile-email" value={profile?.email} onChange={(v) => setP("email", v)} />
              <PField label="Company" testid="driver-profile-company" value={profile?.companyName} onChange={(v) => setP("companyName", v)} />
              <PField label="Plate" testid="driver-profile-plate" value={profile?.plate} onChange={(v) => setP("plate", v)} />
              <PField label="Truck type" testid="driver-profile-trucktype" value={profile?.truckType} onChange={(v) => setP("truckType", v)} />
              <PField label="Trailer type" testid="driver-profile-trailertype" value={profile?.trailerType} onChange={(v) => setP("trailerType", v)} />
              <PField label="MC #" testid="driver-profile-mc" value={profile?.mcNumber} onChange={(v) => setP("mcNumber", v)} />
              <PField label="DOT #" testid="driver-profile-dot" value={profile?.dotNumber} onChange={(v) => setP("dotNumber", v)} />
            </div>
            <Button data-testid="driver-profile-save" onClick={saveProfile} disabled={savingProfile}
              className="mt-5 gap-2 bg-slate-900 font-bold hover:bg-slate-800">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save details
            </Button>
          </Card>
        </section>

        {/* History */}
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <HistoryIcon className="h-5 w-5 text-[#A0148C]" /> Past Stays
          </h2>
          <Card className="divide-y divide-slate-100 border-slate-200 bg-white">
            {data?.history?.length ? (
              data.history.map((s) => (
                <div key={s.id} data-testid={`driver-history-row-${s.id}`} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="font-semibold text-slate-800">{s.lotName} · Spot #{s.spotNumber}</p>
                    <p className="text-xs text-slate-500">
                      {s.ticketNumber && <span className="mr-2 font-data text-slate-700">#{s.ticketNumber}</span>}
                      {new Date(s.checkInAt).toLocaleDateString()} → {s.checkOutAt ? new Date(s.checkOutAt).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  <span className="font-data font-bold text-slate-900">${(s.amountCharged || 0).toFixed(2)}</span>
                </div>
              ))
            ) : (
              <p className="px-5 py-6 text-center text-slate-500">No past stays yet.</p>
            )}
          </Card>
        </section>

        <p className="pt-4 text-center text-xs text-slate-400">
          {data?.businessName || "Discount Truck Parking"} · truckstopdiscount.com · {data?.businessPhone || "954-457-2000"}
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-data ${bold ? "text-lg font-bold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

function PField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="h-11 border-slate-300 bg-white text-slate-900" />
    </div>
  );
}
